import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the message-from-it start screen. Three
 * short slides (what/who, main risks, key terms) shown in the reader while the
 * 3D room downloads. Content only; the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a chat message from "IT" sent by an External account             */
/* ------------------------------------------------------------------ */
function ChatImpersonationArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: a message in a work chat" aria="A work chat message from an account named IT Service Desk that is marked External, asking the reader to reply YES to start a support session" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Work chat
      </text>
      <text x="344" y="53" fontSize="11" fill={C.muted} textAnchor="end">
        Direct message
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <circle cx="30" cy="94" r="14" fill={C.panel2} stroke={C.line} />
      <text x="30" y="98" fontSize="10.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        IT
      </text>
      <text x="54" y="91" fontSize="13" fontWeight="700" fill={C.text}>
        IT Service Desk
      </text>
      <rect className="sr-anim-pulse" x="150" y="78" width="58" height="18" rx="9" fill={C.amber} fillOpacity="0.14" stroke={C.amber} strokeOpacity="0.5" />
      <text x="179" y="90.5" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        External
      </text>
      <text x="54" y="107" fontSize="10.5" fill={C.muted}>
        Today, 16:49
      </text>

      <rect x="54.5" y="116.5" width="289" height="72" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="67" y="136" fontSize="12" fill={C.text}>
        Hi Alex, a security update is due.
      </text>
      <text x="67" y="153" fontSize="12" fill={C.text}>
        Please reply YES to start a support
      </text>
      <text x="67" y="170" fontSize="12" fill={C.text}>
        session before you leave today.
      </text>

      <rect x="16.5" y="200.5" width="327" height="34" rx="9" fill={C.code} stroke={C.line} />
      <text x="29" y="222" fontSize="12.5" fill={C.text}>
        YES
      </text>
      <rect className="sr-anim-blink" x="56" y="210" width="1.5" height="15" rx="0.75" fill={C.mint} />
      <circle cx="324" cy="217.5" r="11" fill={C.mint} fillOpacity="0.14" />
      <polyline points="320,217.5 324,213.5 328,217.5" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="324" y1="213.8" x2="324" y2="222" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" />

      <rect x="16.5" y="248.5" width="327" height="34" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="265.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="265.5" r="1.8" fill={C.amber} />
      <text x="46" y="269.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Looks like IT. Sent from outside.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. a permission request with a scan over the access it asks for     */
/* ------------------------------------------------------------------ */
function PermissionRow({ y, label }: { y: number; label: string }): JSX.Element {
  return (
    <g>
      <circle cx="28" cy={y} r="7" fill={C.red} fillOpacity="0.14" />
      <circle cx="28" cy={y} r="2.4" fill={C.red} />
      <text x="46" y={y + 4.2} fontSize="12.5" fill={C.text}>
        {label}
      </text>
    </g>
  );
}

function PermissionArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label="Example: a permission request" aria="A permission request from an unfamiliar document app asking to read email and files, send email as the user and keep access, with Cancel and Accept buttons" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Permissions requested
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />
      <text x="16" y="88" fontSize="11.5" fill={C.muted}>
        An app you have not used wants to:
      </text>

      <rect x="16.5" y="98.5" width="327" height="124" rx="8" fill={C.code} stroke={C.line} />
      <PermissionRow y={118} label="Read your email" />
      <line x1="30" y1="132.5" x2="330" y2="132.5" stroke={C.line} strokeOpacity="0.6" />
      <PermissionRow y={148} label="Read the files you can open" />
      <line x1="30" y1="162.5" x2="330" y2="162.5" stroke={C.line} strokeOpacity="0.6" />
      <PermissionRow y={178} label="Send email as you" />
      <line x1="30" y1="192.5" x2="330" y2="192.5" stroke={C.line} strokeOpacity="0.6" />
      <PermissionRow y={208} label="Keep this access when you are away" />
      <clipPath id="mfit-perm-clip">
        <rect x="17" y="99" width="326" height="122" rx="7" />
      </clipPath>
      <g clipPath="url(#mfit-perm-clip)">
        <rect className="sr-anim-sweep" x="0" y="99" width="34" height="122" fill={C.red} fillOpacity="0.12" />
      </g>

      <rect className="sr-anim-pulse" x="16.5" y="236.5" width="152" height="32" rx="9" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" />
      <text x="92.5" y="257" fontSize="12.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        Cancel
      </text>
      <rect x="182.5" y="236.5" width="161" height="32" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="263" y="257" fontSize="12.5" fontWeight="700" fill={C.muted} textAnchor="middle">
        Accept
      </text>
      <text x="180" y="288" fontSize="10.5" fill={C.muted} textAnchor="middle">
        Accept gives an app access that stays.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. where does the link really go: real host vs lookalike            */
/* ------------------------------------------------------------------ */
function LinkCheckArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: a link in a chat message" aria="A chat message with an urgent link whose address is compared with the real company file host, showing a lookalike address, and a reminder to verify by calling a known number" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Chat message
      </text>
      <rect x="290" y="38" width="54" height="20" rx="10" fill={C.amber} fillOpacity="0.14" stroke={C.amber} strokeOpacity="0.45" />
      <text x="317" y="52" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        Urgent
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="82" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="29" y="100" fontSize="12" fill={C.text}>
        Can you sign this off before 5?
      </text>
      <rect x="27.5" y="112.5" width="233" height="26" rx="6" fill={C.code} stroke={C.line} />
      <text x="37" y="129" fontSize="11" fontFamily={MONO} fill={C.soft} textDecoration="underline">
        securedocs-northbay.com/v/8f3k2
      </text>
      <clipPath id="mfit-link-clip">
        <rect x="28" y="113" width="232" height="25" rx="5" />
      </clipPath>
      <g clipPath="url(#mfit-link-clip)">
        <rect className="sr-anim-sweep" x="0" y="113" width="26" height="25" fill={C.amber} fillOpacity="0.22" />
      </g>
      <text x="29" y="154" fontSize="10.5" fill={C.muted}>
        {"Sent from a colleague\u2019s account"}
      </text>

      <rect x="16.5" y="174.5" width="327" height="34" rx="8" fill={C.panel2} stroke={C.line} />
      <circle cx="33" cy="191.5" r="7" fill={C.mint} fillOpacity="0.16" />
      <polyline points="29.5,191.5 32,194 36.5,188.7" fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="48" y="195.5" fontSize="10.5" fill={C.muted}>
        Real file host
      </text>
      <text x="128" y="195.5" fontSize="11" fontFamily={MONO} fill={C.soft}>
        northbayfinancial.com
      </text>

      <rect x="16.5" y="216.5" width="327" height="34" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <circle cx="33" cy="233.5" r="7" fill={C.red} fillOpacity="0.16" />
      <line x1="30" y1="230.5" x2="36" y2="236.5" stroke={C.red} strokeWidth="1.7" strokeLinecap="round" />
      <line x1="36" y1="230.5" x2="30" y2="236.5" stroke={C.red} strokeWidth="1.7" strokeLinecap="round" />
      <text x="48" y="237.5" fontSize="10.5" fill={C.muted}>
        This link
      </text>
      <text x="128" y="237.5" fontSize="11" fontFamily={MONO} fill={C.red}>
        securedocs-northbay.com
      </text>

      <rect className="sr-anim-pulse" x="16.5" y="259.5" width="327" height="26" rx="13" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <text x="180" y="276" fontSize="11.5" fontWeight="600" fill={C.mint} textAnchor="middle">
        Check first: call a number you already know
      </text>
    </Frame>
  );
}

export const MESSAGE_FROM_IT_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "The message looks like it\u2019s from someone you trust.",
    illustration: ChatImpersonationArt,
    caption: "Anyone can name a chat account \u201cIT\u201d.",
    sections: [
      {
        h: "What this is about",
        body: "It is Friday afternoon, the last day before a holiday. A chat message arrives from \u201cIT\u201d, then a request from a colleague, then a sign-in screen. You decide what to do with each one, and then see the attack from the attacker\u2019s side.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who uses work chat and shared documents, especially finance teams and people who travel or work from personal devices. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Attackers now work inside the tools you already trust. The message, the account and even the sign-in page can look completely normal. A filter cannot tell whether a request makes sense for you. You can.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "How a trusted tool gets used against you.",
    illustration: PermissionArt,
    caption: "Accept is a permission, not a formality.",
    sections: [
      {
        h: "A fake IT desk in chat",
        body: "A message from \u201cIT Service Desk\u201d asks you to reply or start a support session. A support session can hand an attacker control of your PC. Check who really sent it before you answer.",
      },
      {
        h: "A colleague who isn\u2019t your colleague",
        body: "If an attacker takes over a real account, the message comes from a real name and knows real details. A familiar sender is not proof. A sudden, unusual request is the warning sign.",
      },
      {
        h: "A real sign-in page, a hostile request",
        body: "The page may be genuine, but the app behind it is not. Clicking Accept can let it read your email and files, send mail as you and keep that access after you sign out.",
      },
      {
        h: "Urgency does the rest",
        body: "\u201cBefore you leave\u201d and \u201cbefore Monday\u201d are there to stop you checking. If you are rushed, that is your cue to slow down.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: LinkCheckArt,
    caption: "Check the sender, the link and the request.",
    sections: [
      { h: "Impersonation", body: "Pretending to be IT, a manager or a colleague so you trust a request you would otherwise question." },
      { h: "External account", body: "An account that belongs to another organisation. Chat tools usually label it, so look for the label." },
      { h: "Compromised account", body: "A real account taken over by an attacker. The name is right, but the person writing is not." },
      { h: "Consent phishing", body: "Tricking you into approving an app\u2019s permission request, so it gets access without ever needing your password." },
      { h: "Permission (grant)", body: "Access you give an app to your mail, files or contacts. It can stay active until someone revokes it." },
      { h: "Lookalike address", body: "A web address that resembles the real one but is not, such as an extra word or a changed ending." },
      { h: "Verify out of band", body: "Confirm a request through a different route you already trust, such as a known phone number, not the chat itself." },
    ],
  },
];
